"use strict"

import React, { useState, useEffect } from "react"
import moment from "moment"
import PropTypes from "prop-types"
import { Modal, Button } from "react-bootstrap"
import { FormattedMessage, useIntl } from "react-intl"

import ReactSelectionElement from "../../../shared_components/widgets/ReactSelectionElement"
import { API_STATUS, listUtils, Required } from "../../../shared_components/widgets/utils"
import { logToServer } from "../../../ErrorBoundary"
import { addNewObject } from "../../../Loaders"
import { withSampleConfig } from "../../../config/SampleConfigProvider"
import { withAppSettingsConfig } from "../../../config/AppSettingsConfigProvider"
import SampleSchedule from "../SampleScheduleTab/SampleSchedule"
import { getFormattedMessage } from "../../shared_components_registration"
import { useSensePageWidth } from "../SampleScheduleTab/FormSamplePoint"
import { getLocaleFormattedUserString } from "../../internationalization/LocaleFormatted"

const ModalCreateSampleRound = ({
  show,
  onHide,
  onSuccess,
  sampleroundTemplate,
  // AppSettingsConfigProvider
  config,
  activeUsers,
  // --- AppSettingsConfigProvider
  //SampleConfigProvider
  sample_source_types,
  getSamplingPointMethods,
  getSelectedSamplePoints,
  // --- SampleConfigProvider
}) => {
  const pageWidth = useSensePageWidth()

  /* Modal to start a new sample round. */
  const intl = useIntl()
  const [statusCreate, setStatusCreate] = useState(null)
  const [data, setData] = useState({
    description: "",
  })
  const [errors, setErrors] = useState({})
  const [headers, setHeaders] = useState([])

  /* Method selection details */
  const [selectedPoints, setSelectedPoints] = useState([])
  const [selectedDetails, setSelectedDetails] = useState({})
  const [currentUser, setCurrentUser] = useState({})
  const [showSelectionTable, setShowSelectionTable] = useState(false)
  const [disableSubmit, setDisableSubmit] = useState(true)

  const defaultHeaders = [
    {
      id: "samplemanagement.number.of.samples",
      desc: "# Samples",
    },
    {
      id: "samplemanagement.sample.connection.number",
      desc: "Sample Connection Number",
    },
    {
      id: "samplemanagement.stream.name.and.pfd.stream.number",
      desc: "Stream Name and PFD Stream Number",
    },
    { id: "general.reactor", desc: "Reactor(s)" },
    { id: "general.tag", desc: "Tag Name(s)" },
  ]

  useEffect(() => {
    setHeaders(defaultHeaders)
    if (sampleroundTemplate) {
      getSamplingPointMethods(sampleroundTemplate.sampling_point_list)
      setSelectedPoints(sampleroundTemplate.sampling_point_list)
    }

    setCurrentUser(config.user)
  }, [])

  const initializeSelectedDetails = () => {
    const spMap = {}
    const currentTime = moment().format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS)

    selectedPoints.forEach((sp) => {
      // Create an array of samples for each sampling point based on the # of samples to create
      spMap[sp["id"]] = new Array(sp.samples_to_create).fill(null).map(() => {
        const defaultType = sample_source_types.find((obj) => obj.value === sp.default_sample_source_type)
        const spMethods = Object.keys(sp?.methods) || []
        return {
          sp_id: sp["id"],
          sample_source_type: defaultType ? defaultType.display_name : "",
          sampling_time: currentTime,
          methods: spMethods.length === 1 ? spMethods : [],
          taken_by: currentUser.id,
        }
      })
    })

    setSelectedDetails(spMap)
  }

  useEffect(() => {
    initializeSelectedDetails()
  }, [selectedPoints])

  useEffect(() => {
    const allSelectionsComplete = Object.values(selectedDetails || {}).every((samples) =>
      samples.every((detail) => detail.methods.length > 0),
    )

    setDisableSubmit(!allSelectionsComplete)
  }, [selectedDetails])

  useEffect(() => {
    if (showSelectionTable) {
      const methodSelectionHeaders = headers.filter((obj) => obj.id !== "general.tag")
      const conditionalHeaders = [
        { id: "samplemanagement.methods", desc: "Select Method(s)", required: true },
        { id: "samplemanagement.sample.source.type", desc: "Select Sample Source Type", required: true },
        { id: "general.time", desc: "Select Sampling Time", required: true },
        { id: "general.operator", desc: "Operator Sample Taken By", required: true },
      ]
      methodSelectionHeaders.push(...conditionalHeaders)

      setHeaders(methodSelectionHeaders)
    } else {
      setHeaders(defaultHeaders)
    }
  }, [showSelectionTable])

  const handleBack = () => {
    setShowSelectionTable(false)
    if (!sampleroundTemplate) {
      setSelectedPoints([])
      setSelectedDetails({})
    } else {
      initializeSelectedDetails()
    }
  }

  const handleSelections = (sp_id, index, key, value) => {
    if (value) {
      // If 'taken_by' is updated, apply to all samples across all sampling points
      if (key === "taken_by") {
        setSelectedDetails(
          Object.fromEntries(
            Object.entries(selectedDetails).map(([id, smps]) => {
              const updatedSamples = smps.map((sample) => ({ ...sample, taken_by: value }))
              return [id, updatedSamples]
            }),
          ),
        )
      } else {
        if (key === "sampling_time") {
          /*
          Need to format to ensure the expected dt format is provided to the backend.
          On user selecting :00 seconds the :00 is dropped by default leading to `YYYY-MM-DDTHH:mm`
          instead of the desired `YYYY-MM-DDTHH:mm:ss`.
        */
          value = moment(value).format("YYYY-MM-DDTHH:mm:ss")
        }

        setSelectedDetails((prevState) => {
          const samples = [...prevState[sp_id]]

          // Update specific sample detail
          samples[index] = {
            ...samples[index],
            [key]: value,
          }

          return {
            ...prevState,
            [sp_id]: samples,
          }
        })
      }
    }
  }

  const doCreateSampleRound = (event) => {
    event.preventDefault()

    setErrors({})
    let data_post = Object.assign({}, data)
    if (sampleroundTemplate) {
      data_post["sample_round_template_id"] = sampleroundTemplate.id
    }

    // Validate and send to server
    if (selectedPoints.length) {
      let msg = `${intl.formatMessage({
        id: "samplemanagement.confirm-start-round",
      })} :`

      let _list_names = selectedPoints.map((sp) => sp["sample_conn_no"])
      msg += `\n${_list_names.join(", ")}`

      msg += `\n${intl.formatMessage({
        id: "samplemanagement.start-sample-round",
      })}`

      if (!confirm(msg)) {
        return false
      } else {
        setStatusCreate(API_STATUS.LOADING)
        data_post["sampling_point_ids"] = selectedPoints.map((sp) => sp.id)
        data_post["sampling_point_selections"] = Object.values(selectedDetails)
          .flat()
          .map((info) => {
            return {
              ...info,
              sampling_time: moment(info["sampling_time"]).format("YYYY-MM-DDTHH:mm:ssZZ"), // For sending to the backend with tz offset
            }
          })
        data_post["created_by"] = currentUser.id
        addNewObject("/laboratory/sample-management/sample-round/start_new_round/", data_post, true)
          .then((data) => {
            setStatusCreate(API_STATUS.SUCCESS)
            onSuccess(data)
          })
          .catch((error) => {
            setStatusCreate(API_STATUS.ERROR)
            logToServer(`Couldn't start Sample Round: ${error.statusText}`)
            console.error(error.statusText)
            error.text().then((err) => {
              try {
                err = JSON.parse(err)
              } catch (e) {}
              setErrors(err)
            })

            alert(
              intl.formatMessage({
                id: "samplemaangement.error.starting.sample.round",
              }),
            )
          })
      }
    } else {
      alert(
        `${intl.formatMessage({
          id: "samplemanagement.select.at.least.one.sample.point",
        })}!`,
      )
    }
  }

  return (
    <Modal
      size={"lg"}
      dialogClassName={"common-modal"}
      show={show}
      onHide={onHide}
      backdrop={"static"}
      className={"overflow-scroll"}
    >
      {!showSelectionTable ? (
        <>
          <Modal.Header closeButton>
            <Modal.Title>
              <h5>
                <i className={"fa fa-play"} />
                &nbsp;&nbsp;
                {getFormattedMessage("general.start", "Start")}
              </h5>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form
              name={"form_sampleRound"}
              style={{ width: `${Math.max(pageWidth - 100, 850)}px` }}
              onSubmit={(e) => {
                e.preventDefault()
                setSelectedPoints(getSelectedSamplePoints())
                setShowSelectionTable(true)
              }}
            >
              {sampleroundTemplate ? (
                <>
                  <div style={{ marginBottom: "10px" }}>
                    <FormattedMessage
                      id={"samplemanagement.sample.point.in.new.round.message"}
                      description={"Following Sample Points Will Be Used To Create New Sample Round"}
                    />
                    :
                  </div>
                  <SampleSchedule
                    allowSelection={false}
                    allowActions={false}
                    allowSampleCreation={true}
                    largeScreenCutoff={1400}
                    focus_sample_points={selectedPoints}
                  />
                </>
              ) : (
                <>
                  <div style={{ marginBottom: "10px" }}>
                    <FormattedMessage
                      id={"samplemanagement.sample.point.in.new.round.request"}
                      description={"Request To Select SamplePoints To Include In Round"}
                    />
                    :
                  </div>
                  <SampleSchedule
                    allowSelection={true}
                    allowActions={false}
                    allowSampleCreation={true}
                    largeScreenCutoff={1400}
                  />
                </>
              )}
              <div className={`one-rem-margin-bottom ${errors.description ? "has-error" : ""}`}>
                <label>
                  <FormattedMessage id={"general.description"} description={"Description Label"} />:
                </label>
                <input
                  type={"text"}
                  className={"form-control"}
                  name={"description"}
                  value={data.description}
                  placeholder={
                    `${intl.formatMessage({ id: "general.description" })} ` +
                    `(${intl.formatMessage({ id: "general.optional" })})`
                  }
                  onChange={(event) => {
                    setData({
                      ...data,
                      [event.target.name]: event.target.value,
                    })
                  }}
                />
                <span className={"form-text text-danger"}>{errors.description}</span>
              </div>
              <div style={{ margin: "20px 0px 30px" }} align={"center"}>
                <Button variant={"primary"} size={"sm"} type={"submit"}>
                  <span className={"fa fa-play text_white"} />
                  &nbsp;
                  <FormattedMessage id={"general.next"} description={"Make Method Selections Button"} />
                </Button>
              </div>
            </form>
          </Modal.Body>
          <Modal.Footer>
            <div style={{ marginRight: "auto" }}>
              {Object.keys(errors).length !== 0 && (
                <p className={"error-message"}>
                  <FormattedMessage id={"general.server.error"} description={"Server Error Message"} />
                </p>
              )}
            </div>
            <Button variant={"outline-primary"} size={"sm"} onClick={onHide} style={{ marginLeft: "auto" }}>
              <FormattedMessage id={"general.closeButton"} description={"Close Modal Button"} />
            </Button>
          </Modal.Footer>
        </>
      ) : (
        <>
          <Modal.Header closeButton>
            <h5>
              {getFormattedMessage(
                "samplemanagement.select.methods.and.types",
                "Select Analysis Methods and Source Types",
              )}
            </h5>
          </Modal.Header>
          <Modal.Body style={{ width: `${Math.max(pageWidth - 100, 1000)}px` }}>
            <table className={"table table-bordered table-sm tbl_lanzatable"}>
              <thead>
                <tr>
                  {headers.map((header) => (
                    <th
                      className={"text-center"}
                      key={header["id"]}
                      style={{ minWidth: "60px", fontSize: "90%" }}
                    >
                      <FormattedMessage
                        id={header["id"]}
                        description={`${header["desc"]} Column Name`}
                        defaultMessage={header["desc"]}
                      />
                      {header["required"] && <Required />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {selectedPoints.flatMap((sp, i) => {
                  // Create an array with the length of samples_to_create, contents don't matter
                  const sampleRows = new Array(sp.samples_to_create).fill(null)
                  const currentDefaultType =
                    sample_source_types.find((obj) => obj.value === sp.default_sample_source_type) || {}
                  const spMethods = Object.keys(sp?.methods || {}).sort()
                  return sampleRows.map((_, index) => {
                    const spRowInfo = selectedDetails?.[sp["id"]]?.[index] || {}
                    return (
                      <tr key={`${sp.id}_${index}`}>
                        {[
                          index + 1,
                          sp["sample_conn_no"],
                          `${sp["stream_name"]} (${sp["pfd_stream_no"] || "-"})`,
                          listUtils.removeDuplicates(sp["reactor_list"].map((r) => r.name)).join(", "),
                        ].map((val, cIDK) => (
                          <td
                            key={`sp${i}r${index}c${cIDK}`}
                            className={"text-center"}
                            style={{ fontSize: "90%" }}
                          >
                            {val}
                          </td>
                        ))}
                        <td style={{ minWidth: "225px", maxWidth: "325px", fontSize: "90%" }}>
                          <ReactSelectionElement
                            name={sp["id"]}
                            list={spMethods.map((s) => {
                              return { value: s }
                            })}
                            value={(spRowInfo["methods"] || []).map((s) => {
                              return { value: s }
                            })}
                            getOptionLabel={(label) => {
                              const method = sp.methods[label.value]
                              return `${method.name}: ${method.description || "-"}`
                            }}
                            onValueChange={(name, selected_options) =>
                              handleSelections(
                                name,
                                index,
                                "methods",
                                selected_options.map((s) => s.value),
                              )
                            }
                          />
                        </td>
                        <td className={"text-center"}>
                          <select
                            className={"form-select form-select-sm text-center"}
                            name={"sample_source_type"}
                            onChange={(e) =>
                              handleSelections(sp["id"], index, "sample_source_type", e.target.value)
                            }
                            style={{ minWidth: "120px", fontSize: "90%" }}
                            required
                          >
                            <option value={currentDefaultType.display_name}>
                              {currentDefaultType.display_name}
                            </option>
                            {sample_source_types
                              .filter((type) => type !== currentDefaultType.display_name)
                              .map((type, i) => (
                                <option value={type.display_name} key={i}>
                                  {type.display_name}
                                </option>
                              ))}
                          </select>
                        </td>
                        <td className={"text-center"}>
                          <input
                            type={"datetime-local"}
                            className={"form-control form-control-sm text-center"}
                            name={"sampling_time"}
                            value={spRowInfo?.["sampling_time"] || ""}
                            onChange={(e) =>
                              handleSelections(sp["id"], index, "sampling_time", e.target.value)
                            }
                            style={{ fontSize: "90%" }}
                            required
                          />
                        </td>
                        <td className={"text-center"}>
                          <select
                            className={"form-select form-select-sm text-center"}
                            name={"taken_by"}
                            value={currentUser.id}
                            onChange={(e) => {
                              const user = activeUsers.find((user) => user.id.toString() === e.target.value)
                              setCurrentUser(user)
                              handleSelections(sp["id"], index, "taken_by", e.target.value)
                            }}
                            style={{ minWidth: "120px", fontSize: "90%" }}
                            required
                          >
                            <option value={currentUser.id}>
                              {getLocaleFormattedUserString(currentUser, intl)}
                            </option>
                            {activeUsers
                              .filter((user) => user.id !== currentUser.id)
                              .map((user, i) => (
                                <option value={user.id} key={i}>
                                  {getLocaleFormattedUserString(user, intl)}
                                </option>
                              ))}
                          </select>
                        </td>
                      </tr>
                    )
                  })
                })}
              </tbody>
            </table>
          </Modal.Body>
          <Modal.Footer>
            <Button variant={"outline-primary"} size={"sm"} onClick={handleBack}>
              <FormattedMessage id={"general.cancelButton"} description={"Cancel Button"} />
            </Button>
            <Button
              variant={"primary"}
              size={"sm"}
              type={"submit"}
              onClick={doCreateSampleRound}
              disabled={disableSubmit || statusCreate === API_STATUS.LOADING}
            >
              {statusCreate === API_STATUS.LOADING ? (
                <>
                  <div className="spinner-border spinner-border-sm text_white text-center" role="status" />
                  &nbsp;
                  {getFormattedMessage("general.loading", "Loading")}...
                </>
              ) : (
                <>
                  <span className={"fa fa-play text_white"} />
                  &nbsp;
                  {getFormattedMessage("general.start", "Start")}
                </>
              )}
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  )
}

export default withSampleConfig(withAppSettingsConfig(ModalCreateSampleRound))

ModalCreateSampleRound.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  sampleroundTemplate: PropTypes.object,
  config: PropTypes.object.isRequired,
  sample_source_types: PropTypes.array.isRequired,
  getSamplingPointMethods: PropTypes.func.isRequired,
  getSelectedSamplePoints: PropTypes.func.isRequired,
}
